import { MundialMatchType } from '../types/mundial';

export interface KnockoutRoundConfig {
  matchType: MundialMatchType;
  label: string;
  phaseLabel: string;
  slotCount: number;
}

/** Rondas de eliminación directa — formato Mundial FIFA 2026 (48 selecciones → 32 en octavos de la llave) */
export const KNOCKOUT_ROUNDS: KnockoutRoundConfig[] = [
  { matchType: 'round_of_32', label: '16vos de final', phaseLabel: '16vos de final', slotCount: 16 },
  { matchType: 'round_of_16', label: '8vos de final', phaseLabel: '8vos de final', slotCount: 8 },
  { matchType: 'quarter_final', label: 'Cuartos de final', phaseLabel: 'Cuartos de final', slotCount: 4 },
  { matchType: 'semi_final', label: 'Semifinal', phaseLabel: 'Semifinal', slotCount: 2 },
  { matchType: 'third_place', label: 'Tercer lugar', phaseLabel: 'Tercer lugar', slotCount: 1 },
  { matchType: 'final', label: 'Final', phaseLabel: 'Final', slotCount: 1 },
];

/**
 * Plantilla de cruces por slot (R32-01 … R32-16).
 * En producción la fuente de verdad es Firestore: fecha + bracket_slot + source_slot_a/b
 * cargados desde el Excel de calendario. Este mapa solo define qué origen corresponde a cada slot.
 */
export const ROUND_OF_32_PAIRINGS: {
  slot: string;
  sourceA: string;
  sourceB: string;
  labelA: string;
  labelB: string;
}[] = [
  { slot: 'R32-01', sourceA: '2A', sourceB: '2B', labelA: '2A', labelB: '2B' },
  { slot: 'R32-02', sourceA: '1E', sourceB: '3ABCDF', labelA: '1E', labelB: '3 ABCDF' },
  { slot: 'R32-03', sourceA: '1F', sourceB: '2C', labelA: '1F', labelB: '2C' },
  { slot: 'R32-04', sourceA: '1C', sourceB: '2F', labelA: '1C', labelB: '2F' },
  { slot: 'R32-05', sourceA: '1I', sourceB: '3CDFGH', labelA: '1I', labelB: '3 CDFGH' },
  { slot: 'R32-06', sourceA: '2E', sourceB: '2I', labelA: '2E', labelB: '2I' },
  { slot: 'R32-07', sourceA: '1H', sourceB: '2J', labelA: '1H', labelB: '2J' },
  { slot: 'R32-08', sourceA: '1G', sourceB: '3AEHIJ', labelA: '1G', labelB: '3 AEHIJ' },
  { slot: 'R32-09', sourceA: '1D', sourceB: '3BEFIJ', labelA: '1D', labelB: '3 BEFIJ' },
  { slot: 'R32-10', sourceA: '1B', sourceB: '3EFGHIJ', labelA: '1B', labelB: '3 EFGHIJ' },
  { slot: 'R32-11', sourceA: '1J', sourceB: '2H', labelA: '1J', labelB: '2H' },
  { slot: 'R32-12', sourceA: '1K', sourceB: '3DEIJL', labelA: '1K', labelB: '3 DEIJL' },
  { slot: 'R32-13', sourceA: '2K', sourceB: '2L', labelA: '2K', labelB: '2L' },
  { slot: 'R32-14', sourceA: '1L', sourceB: '3EHIJK', labelA: '1L', labelB: '3 EHIJK' },
  { slot: 'R32-15', sourceA: '1A', sourceB: '3CEFHI', labelA: '1A', labelB: '3 CEFHI' },
  { slot: 'R32-16', sourceA: '2D', sourceB: '2G', labelA: '2D', labelB: '2G' },
];

/** Orden visual del bracket (pares adyacentes alimentan el mismo R16) */
export const BRACKET_LEFT_R32 = [
  'R32-01', 'R32-03', 'R32-02', 'R32-05', 'R32-04', 'R32-06', 'R32-07', 'R32-08',
];
export const BRACKET_RIGHT_R32 = [
  'R32-11', 'R32-12', 'R32-09', 'R32-10', 'R32-14', 'R32-16', 'R32-13', 'R32-15',
];
export const BRACKET_LEFT_R16 = ['R16-01', 'R16-02', 'R16-03', 'R16-04'];
export const BRACKET_RIGHT_R16 = ['R16-05', 'R16-06', 'R16-07', 'R16-08'];
export const BRACKET_LEFT_QF = ['QF-01', 'QF-02'];
export const BRACKET_RIGHT_QF = ['QF-03', 'QF-04'];
export const BRACKET_LEFT_SF = ['SF-01'];
export const BRACKET_RIGHT_SF = ['SF-02'];
export const BRACKET_LEFT_GROUPS = ['A', 'B', 'C', 'D', 'E', 'F'];
export const BRACKET_RIGHT_GROUPS = ['G', 'H', 'I', 'J', 'K', 'L'];

export const GROUP_BRACKET_COLORS: Record<string, string> = {
  A: '#22c55e',
  B: '#ef4444',
  C: '#f97316',
  D: '#3b82f6',
  E: '#a855f7',
  F: '#eab308',
  G: '#ec4899',
  H: '#14b8a6',
  I: '#d946ef',
  J: '#38bdf8',
  K: '#f43f5e',
  L: '#06b6d4',
};

export const PAIRING_BY_SLOT = new Map(
  ROUND_OF_32_PAIRINGS.map((p) => [p.slot, p])
);

/** Avance: ganador del slot → siguiente partido (alineado al bracket visual) */
export const BRACKET_ADVANCEMENT: Record<
  string,
  { nextSlot: string; position: 'team_a' | 'team_b' }
> = {
  'R32-01': { nextSlot: 'R16-01', position: 'team_a' },
  'R32-03': { nextSlot: 'R16-01', position: 'team_b' },
  'R32-02': { nextSlot: 'R16-02', position: 'team_a' },
  'R32-05': { nextSlot: 'R16-02', position: 'team_b' },
  'R32-04': { nextSlot: 'R16-03', position: 'team_a' },
  'R32-06': { nextSlot: 'R16-03', position: 'team_b' },
  'R32-07': { nextSlot: 'R16-04', position: 'team_a' },
  'R32-08': { nextSlot: 'R16-04', position: 'team_b' },
  'R32-11': { nextSlot: 'R16-05', position: 'team_a' },
  'R32-12': { nextSlot: 'R16-05', position: 'team_b' },
  'R32-09': { nextSlot: 'R16-06', position: 'team_a' },
  'R32-10': { nextSlot: 'R16-06', position: 'team_b' },
  'R32-14': { nextSlot: 'R16-07', position: 'team_a' },
  'R32-16': { nextSlot: 'R16-07', position: 'team_b' },
  'R32-13': { nextSlot: 'R16-08', position: 'team_a' },
  'R32-15': { nextSlot: 'R16-08', position: 'team_b' },
  'R16-01': { nextSlot: 'QF-01', position: 'team_a' },
  'R16-02': { nextSlot: 'QF-01', position: 'team_b' },
  'R16-03': { nextSlot: 'QF-02', position: 'team_a' },
  'R16-04': { nextSlot: 'QF-02', position: 'team_b' },
  'R16-05': { nextSlot: 'QF-03', position: 'team_a' },
  'R16-06': { nextSlot: 'QF-03', position: 'team_b' },
  'R16-07': { nextSlot: 'QF-04', position: 'team_a' },
  'R16-08': { nextSlot: 'QF-04', position: 'team_b' },
  'QF-01': { nextSlot: 'SF-01', position: 'team_a' },
  'QF-02': { nextSlot: 'SF-01', position: 'team_b' },
  'QF-03': { nextSlot: 'SF-02', position: 'team_a' },
  'QF-04': { nextSlot: 'SF-02', position: 'team_b' },
  'SF-01': { nextSlot: 'FINAL', position: 'team_a' },
  'SF-02': { nextSlot: 'FINAL', position: 'team_b' },
};

export const PENDING_TEAM_LABEL = 'Por definir';

/** Orden de slots para UI (captura y bracket) */
export const BRACKET_SLOT_DISPLAY_ORDER = [
  ...BRACKET_LEFT_R32,
  ...BRACKET_RIGHT_R32,
  ...BRACKET_LEFT_R16,
  ...BRACKET_RIGHT_R16,
  ...BRACKET_LEFT_QF,
  ...BRACKET_RIGHT_QF,
  ...BRACKET_LEFT_SF,
  ...BRACKET_RIGHT_SF,
  '3RD',
  'THIRD',
  'TP-01',
  'FINAL',
];
